import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  VStack,
  List,
  ListItem,
  ListIcon,
  Text,
  Box,
  Icon
} from "@chakra-ui/react";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import { Form, Input } from "components";
import { useHistory } from "react-router";
import { changePassword } from "api/webservice/auth";
import { useToast } from "hooks";
import { useUser } from "context/user";
import {
  CATEGORY_RULES,
  MIN_LENGTH,
  REQUIRED_CATEGORY_COUNT,
  countCategories,
  containsIdentity,
  validatePassword
} from "./passwordValidation";

type FormData = {
  currentPassword: string;
  newPassword: string;
};

type RuleIndicatorProps = {
  label: string;
  met: boolean;
};

const RuleIndicator: React.FC<RuleIndicatorProps> = ({ label, met }) => (
  <ListItem
    fontSize="sm"
    color={met ? "green.500" : "gray.500"}
    display="flex"
    alignItems="center"
  >
    <ListIcon
      as={met ? AiOutlineCheckCircle : AiOutlineCloseCircle}
      color={met ? "green.500" : "gray.400"}
    />
    {label}
  </ListItem>
);

export const ChangePasswordForm: React.FC = () => {
  const history = useHistory();
  const { handleSubmit, errors, register, formState, watch } = useForm<
    FormData
  >();
  const toast = useToast();
  const { user } = useUser()!;

  // Server-side validation errors (e.g. password history reuse) returned by
  // the backend as { message, errors: [] }.
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  // Logged-in user's identity drives the "must not contain your username or
  // name" live-feedback rule; server enforces it authoritatively regardless.
  const identity = {
    username: user?.username,
    firstName: user?.first_name,
    lastName: user?.last_name
  };

  const passwordValue = watch("newPassword") ?? "";
  const categoriesMet = countCategories(passwordValue);
  const hasIdentity = !!passwordValue && containsIdentity(passwordValue, identity);

  const validateCurrentPassword = (currentPasswordValue: string) => {
    if (!currentPasswordValue) {
      return "Current password is required";
    }
    return true;
  };

  const submitForm = async ({
    currentPassword,
    newPassword
  }: FormData): Promise<any | null> => {
    setServerErrors([]);

    await changePassword(currentPassword, newPassword)
      .then(() => {
        toast({
          title: "Password updated",
          description: "Your password has been changed.",
          status: "success"
        });
        history.replace("/dashboard");
      })
      .catch(error => {
        // The backend returns validation/history failures as HTTP 400 with a
        // { response: { message, errors: [] } } body. Other failures return a
        // plain string in `response`.
        const serverResponse = error?.response?.data?.response;

        if (serverResponse && typeof serverResponse === "object") {
          const messages: string[] = Array.isArray(serverResponse.errors)
            ? serverResponse.errors
            : [];
          setServerErrors(messages);
          toast({
            title: "Problem updating password",
            description:
              serverResponse.message ?? "Please review the requirements below.",
            status: "error"
          });
        } else {
          toast({
            title: "Problem updating password",
            description:
              typeof serverResponse === "string"
                ? serverResponse
                : "Please try again.",
            status: "error"
          });
        }
      });
  };

  return (
    <Form onSubmit={handleSubmit(submitForm)}>
      <VStack spacing={6} align="stretch">
        <Input
          label="Current Password"
          name="currentPassword"
          type="password"
          inputIcon="password"
          register={register({ validate: validateCurrentPassword })}
          errorMessage={errors.currentPassword?.message}
        />

        <Input
          label="New Password"
          name="newPassword"
          type="password"
          inputIcon="edit"
          register={register({
            validate: value => validatePassword(value, identity)
          })}
          errorMessage={errors.newPassword?.message}
        />

        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={2}>
            Password must meet the following requirements:
          </Text>
          <List spacing={1}>
            <RuleIndicator
              label={`At least ${MIN_LENGTH} characters`}
              met={passwordValue.length >= MIN_LENGTH}
            />
            <RuleIndicator
              label={`At least ${REQUIRED_CATEGORY_COUNT} of the following (${categoriesMet}/${CATEGORY_RULES.length}):`}
              met={categoriesMet >= REQUIRED_CATEGORY_COUNT}
            />
            <Box pl={6}>
              <List spacing={1}>
                {CATEGORY_RULES.map(rule => (
                  <RuleIndicator
                    key={rule.label}
                    label={rule.label}
                    met={rule.isMet(passwordValue)}
                  />
                ))}
              </List>
            </Box>
            <RuleIndicator
              label="Does not contain your username or name"
              met={!hasIdentity}
            />
          </List>
        </Box>

        {serverErrors.length > 0 && (
          <Box
            borderWidth="1px"
            borderColor="red.300"
            borderRadius="md"
            bg="red.50"
            p={3}
          >
            <List spacing={1}>
              {serverErrors.map(message => (
                <ListItem
                  key={message}
                  fontSize="sm"
                  color="red.600"
                  display="flex"
                  alignItems="center"
                >
                  <Icon as={AiOutlineCloseCircle} color="red.500" mr={2} />
                  {message}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Button
          disabled={formState.isSubmitting}
          variant="blue"
          size="md"
          type="submit"
        >
          Change Password
        </Button>
      </VStack>
    </Form>
  );
};
